"use client";

import { useId, useMemo, useState } from "react";
import { CheckIcon, EyeIcon, EyeOffIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PasswordInputProps extends React.ComponentProps<"input"> {
  hideStrengthUI?: boolean;
}

export default function PasswordInput({ className, value: valueProp, onChange: onChangeProp, hideStrengthUI = false, ...rest }: PasswordInputProps) {

  // Hooks
  const { t } = useTranslation("components");
  const id = useId();

  // States
  const [password, setPassword] = useState("");
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  const checkStrength = (pass: string) => {
    const requirements = [
      { regex: /.{8,}/, text: t("passwordinput.requirements.atleast8characters") },
      { regex: /[0-9]/, text: t("passwordinput.requirements.atleast1number") },
      { regex: /[a-z]/, text: t("passwordinput.requirements.atleast1lowercase") },
      { regex: /[A-Z]/, text: t("passwordinput.requirements.atleast1uppercase") },
    ];

    return requirements.map((req) => ({
      met: req.regex.test(pass),
      text: req.text,
    }));
  };

  const isControlled = valueProp !== undefined;
  const displayValue = isControlled ? String(valueProp ?? "") : password;

  const strength = checkStrength(displayValue);

  const strengthScore = useMemo(() => {
    return strength.filter((req) => req.met).length;
  }, [strength]);

  const getStrengthColor = (score: number) => {
    if (score === 0) return "bg-border";
    if (score <= 1) return "bg-red-500";
    if (score <= 2) return "bg-orange-500";
    if (score === 3) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getStrengthText = (score: number) => {
    if (score === 0) return t("passwordinput.enterpassword");
    if (score <= 2) return t("passwordinput.weakpassword");
    if (score === 3) return t("passwordinput.mediumpassword");
    return t("passwordinput.strongpassword");
  };

  return (
    <div>
      {/* Password input field with toggle visibility button */}
      <div className="*:not-first:mt-2">
        <div className="relative">
          <Input
            id={id}
            className={cn("pe-9", className)}
            placeholder={t("passwordinput.placeholder")}
            type={isVisible ? "text" : "password"}
            value={displayValue}
            onChange={(e) => {
              if (!isControlled) setPassword(e.target.value);
              if (onChangeProp) onChangeProp(e);
            }}
            aria-describedby={`${id}-description`}
            {...rest}
          />
          <button
            className="text-muted-foreground/80 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none focus:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={toggleVisibility}
            aria-label={isVisible ? t("passwordinput.hidepassword") : t("passwordinput.showpassword")}
            aria-pressed={isVisible}
            aria-controls="password"
          >
            {isVisible ? (
              <EyeOffIcon size={16} aria-hidden="true" />
            ) : (
              <EyeIcon size={16} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Password strength indicator */}
      {!hideStrengthUI && (
        <>
          <div
            className="bg-border mt-3 mb-4 h-1 w-full overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={strengthScore}
            aria-valuemin={0}
            aria-valuemax={4}
            aria-label={t("passwordinput.passwordstrength")}
          >
            <div
              className={`h-full ${getStrengthColor(strengthScore)} transition-all duration-500 ease-out`}
              style={{ width: `${(strengthScore / 4) * 100}%` }}
            ></div>
          </div>

          {/* Password strength description */}
          <p
            id={`${id}-description`}
            className="text-foreground mb-2 text-sm font-medium"
          >
            {getStrengthText(strengthScore)}. {t("passwordinput.mustcontain")}
          </p>

          {/* Password requirements list */}
          <ul className="space-y-1.5" aria-label={t("passwordinput.passwordrequirements")}>
            {strength.map((req, index) => (
              <li key={index} className="flex items-center gap-2">
                {req.met ? (
                  <CheckIcon
                    size={16}
                    className="text-emerald-500"
                    aria-hidden="true"
                  />
                ) : (
                  <XIcon
                    size={16}
                    className="text-muted-foreground/80"
                    aria-hidden="true"
                  />
                )}
                <span
                  className={`text-xs ${req.met ? "text-emerald-600" : "text-muted-foreground"}`}
                >
                  {req.text}
                  <span className="sr-only">
                    {req.met ? t("passwordinput.accessibility.requirementmet") : t("passwordinput.accessibility.requirementnotmet")}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
